import React, { useState, useRef, useEffect,useCallback } from 'react';
import { Camera, X, SwitchCamera, RotateCcw, Check } from 'lucide-react';
import CameraService from '../utils/CameraService';
import PermissionHandler from './PermissionHandler';
import usePerformanceMonitor from '../hooks/usePerformanceMonitor';

const CameraModal = ({ isOpen, onClose, onCapture }) => {
  const [permissionState, setPermissionState] = useState('checking');
  const [cameraState, setCameraState] = useState('idle');
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [isRetaking, setIsRetaking] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraServiceRef = useRef(null);
  
  // Performance monitoring
  const {
    startInitTimer,
    endInitTimer,
    startCaptureTimer,
    endCaptureTimer,
    recordMemoryUsage,
    resetMetrics
  } = usePerformanceMonitor();

  // Initialize camera service
  useEffect(() => {
    if (!cameraServiceRef.current) {
      cameraServiceRef.current = new CameraService();
    }
  }, []);

  // Check permissions and initialize camera when modal opens
  useEffect(() => {
    if (isOpen && cameraServiceRef.current) {
      initializeCamera();
    } else if (!isOpen) {
      cleanup();
    }

    return () => {
      if (!isOpen) {
        cleanup();
      }
    };
  }, [isOpen]);

  const initializeCamera = async () => {
    try {
      setPermissionState('checking');
      setCameraState('initializing');
      setError(null);
      
      // Start performance monitoring
      startInitTimer();
      recordMemoryUsage();
      
      // Always request permission to establish camera stream
      // checkPermission() only checks if permission was previously granted,
      // but doesn't establish the actual camera stream needed for initialize()
      try {
        const permissionGranted = await cameraServiceRef.current.requestPermission();
        if (!permissionGranted.success) {
          setPermissionState('denied');
          setError(permissionGranted.error || 'Camera permission denied');
          return;
        }
      } catch (permissionError) {
        console.error('Permission request error:', permissionError);
        setPermissionState('denied');
        setError(permissionError.message || 'Camera permission request failed');
        return;
      }
      
      setPermissionState('granted');
      
      // Get available cameras
      const cameras = await cameraServiceRef.current.getAvailableDevices();
      setAvailableCameras(cameras);
      
      // Initialize camera with video element - stream is now available from requestPermission
      await cameraServiceRef.current.initialize(videoRef.current);
      
      setCameraState('ready');
      
      // End performance monitoring
      endInitTimer();
      recordMemoryUsage();
    } catch (err) {
      console.error('Camera initialization error:', err);
      
      // Handle different types of errors appropriately
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setPermissionState('denied');
        setError('Camera permission was denied. Please allow camera access and try again.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setPermissionState('error');
        setError('No camera device found. Please connect a camera and try again.');
      } else if (err.name === 'NotReadableError') {
        setPermissionState('error');
        setError('Camera is already in use by another application. Please close other apps using the camera.');
      } else {
        setPermissionState('error');
        setError(err.message || 'Failed to initialize camera. Please try again.');
      }
    }
  };

  const capturePhoto = async () => {
    if (!cameraServiceRef.current || cameraState !== 'ready') return;
    
    try {
      setCameraState('capturing');
      
      // Start capture performance monitoring
      startCaptureTimer();
      recordMemoryUsage();
      
      // Capture photo with optimized quality settings
      const photoData = await cameraServiceRef.current.capturePhoto({
        quality: 0.85, // Balanced quality vs file size
        maxWidth: 1920, // Limit max resolution for performance
        maxHeight: 1080
      });
      
      // Clean up previous photo URL to prevent memory leaks
      if (capturedPhoto?.url) {
        URL.revokeObjectURL(capturedPhoto.url);
      }
      
      setCapturedPhoto({
        blob: photoData.blob,
        url: URL.createObjectURL(photoData.blob),
        width: photoData.width,
        height: photoData.height,
        size: photoData.size
      });
      
      setCameraState('preview');
      
      // End capture performance monitoring
      endCaptureTimer();
      recordMemoryUsage();
      
      // Log photo info in development
      if (process.env.NODE_ENV === 'development') {
        const sizeMB = (photoData.size / 1024 / 1024).toFixed(2);
        console.log(`Photo captured: ${photoData.width}x${photoData.height}, ${sizeMB}MB`);
      }
    } catch (err) {
      console.error('Photo capture error:', err);
      setError('Failed to capture photo. Please try again.');
      setCameraState('ready');
    }
  };

  const retakePhoto = async () => {
    setIsRetaking(true);
    
    // Clean up previous photo
    if (capturedPhoto?.url) {
      URL.revokeObjectURL(capturedPhoto.url);
    }
    setCapturedPhoto(null);
    
    try {
      // Reinitialize camera
      await cameraServiceRef.current.initialize(videoRef.current);
      setCameraState('ready');
    } catch (err) {
      console.error('Camera reinitialization error:', err);
      setError('Failed to restart camera. Please try again.');
    } finally {
      setIsRetaking(false);
    }
  };

  const switchCamera = async () => {
    if (availableCameras.length <= 1) return;
    
    try {
      setCameraState('initializing');
      const nextIndex = (currentCameraIndex + 1) % availableCameras.length;
      setCurrentCameraIndex(nextIndex);
      
      await cameraServiceRef.current.switchCamera(
        availableCameras[nextIndex].deviceId
      );
      
      setCameraState('ready');
    } catch (err) {
      console.error('Camera switch error:', err);
      setError('Failed to switch camera');
      setCameraState('error');
    }
  };

  const confirmPhoto = () => {
    if (capturedPhoto && onCapture) {
      // Create a File object from the blob
      const file = new File([capturedPhoto.blob], `camera-photo-${Date.now()}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      onCapture(file);
      handleClose();
    }
  };

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const cleanup = useCallback(() => {
    try {
      // Stop camera service and cleanup streams
      if (cameraServiceRef.current) {
        cameraServiceRef.current.cleanup();
      }
      
      // Stop video element and clear source
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.load();
      }
      
      // Revoke object URLs to prevent memory leaks
      if (capturedPhoto?.url) {
        URL.revokeObjectURL(capturedPhoto.url);
      }
      
      // Reset all state
      setCapturedPhoto(null);
      setCameraState('idle');
      setPermissionState('checking');
      setError(null);
      setCurrentCameraIndex(0);
      setAvailableCameras([]);
      
      // Reset performance metrics
      resetMetrics();
      
      // Force garbage collection hint (if available)
      if (window.gc && typeof window.gc === 'function') {
        try {
          window.gc();
        } catch (e) {
          // Ignore errors - gc() is not always available
        }
      }
    } catch (err) {
      console.error('Cleanup error:', err);
    }
  }, [capturedPhoto?.url, resetMetrics]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {cameraState === 'preview' ? 'Photo Preview' : 'Take Photo'}
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Permission Handler */}
          {(permissionState === 'checking' || permissionState === 'denied' || permissionState === 'error') && (
            <PermissionHandler
              permissionState={permissionState}
              error={error}
              onRetry={initializeCamera}
            />
          )}

          {/* Camera View */}
          {permissionState === 'granted' && cameraState !== 'preview' && (
            <div className="relative">
              {/* Video Element */}
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                
                {/* Loading Overlay */}
                {(cameraState === 'initializing' || isRetaking) && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                      <p>{isRetaking ? 'Switching camera...' : 'Initializing camera...'}</p>
                    </div>
                  </div>
                )}
                
                {/* Capturing Overlay */}
                {cameraState === 'capturing' && (
                  <div className="absolute inset-0 bg-white opacity-50 animate-pulse"></div>
                )}
              </div>

              {/* Camera Controls */}
              {cameraState === 'ready' && (
                <div className="flex items-center justify-center mt-4 space-x-4">
                  {/* Switch Camera Button */}
                  {availableCameras.length > 1 && (
                    <button
                      onClick={switchCamera}
                      className="p-3 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      title="Switch Camera"
                    >
                      <SwitchCamera className="w-6 h-6" />
                    </button>
                  )}
                  
                  {/* Capture Button */}
                  <button
                    onClick={capturePhoto}
                    className="p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-full transition-colors"
                    title="Take Photo"
                  >
                    <Camera className="w-8 h-8" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Photo Preview */}
          {cameraState === 'preview' && capturedPhoto && (
            <div className="space-y-4">
              <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                <img
                  src={capturedPhoto.url}
                  alt="Captured photo"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Preview Controls */}
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={retakePhoto}
                  disabled={isRetaking}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Retake</span>
                </button>
                
                <button
                  onClick={confirmPhoto}
                  className="flex items-center space-x-2 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <Check className="w-4 h-4" />
                  <span>Use Photo</span>
                </button>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && cameraState !== 'preview' && permissionState === 'granted' && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
              <button
                onClick={initializeCamera}
                className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Hidden Canvas for Photo Capture */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default CameraModal;